import JSZip from 'jszip';
import type { ExportInboundEmailForDebugOutput } from '@erp/rpc';

/**
 * ZIP layout used by the inbound-email debug export/import round-trip:
 *
 *   inbound-email-<id>-<ts>.zip/
 *   ├── manifest.json
 *   │     // The full export payload, but with `originalAttachments[*].contentBase64`
 *   │     // emptied out — bytes live in the `documents/` tree so the manifest stays
 *   │     // small and human-readable.
 *   └── documents/
 *       └── <documentId>/<safeOriginalFileName>
 *             // Raw attachment / email-body bytes, one file per Document.
 *
 * Keeping pack/unpack here means both the export download flow and the
 * import preview flow agree on the layout.
 */

const MANIFEST_FILE = 'manifest.json';
const DOCUMENTS_PREFIX = 'documents';

/** Mirrors the backend's storage-key sanitization so paths line up. */
function safeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function documentEntryPath(documentId: string, fileName: string): string {
  return `${DOCUMENTS_PREFIX}/${documentId}/${safeFileName(fileName)}`;
}

/**
 * Pack an export payload into a ZIP blob: emit `manifest.json` (with
 * binaries stripped) plus one file per `originalAttachments` entry.
 */
export async function packExportPayloadAsZip(
  payload: ExportInboundEmailForDebugOutput,
): Promise<Blob> {
  const zip = new JSZip();

  const manifest: ExportInboundEmailForDebugOutput = {
    ...payload,
    originalAttachments: payload.originalAttachments.map((att) => ({
      ...att,
      // Bytes live under documents/<id>/...; clear the base64 to keep the manifest small.
      contentBase64: '',
    })),
  };
  zip.file(MANIFEST_FILE, JSON.stringify(manifest, null, 2));

  for (const att of payload.originalAttachments) {
    if (!att.contentBase64) continue;
    const bytes = base64ToUint8Array(att.contentBase64);
    zip.file(documentEntryPath(att.documentId, att.fileName), bytes);
  }

  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

/**
 * Unpack a ZIP file produced by `packExportPayloadAsZip` back into the
 * raw export payload shape (with binaries re-inlined as base64). The
 * caller is expected to validate the result with
 * `ExportInboundEmailForDebugOutputSchema`.
 *
 * Throws on malformed ZIPs (missing manifest, malformed JSON,
 * `originalAttachments` entries pointing at files we can't find).
 */
export async function unpackImportZip(file: File): Promise<unknown> {
  const zip = await JSZip.loadAsync(file);

  const manifestEntry = zip.file(MANIFEST_FILE);
  if (!manifestEntry) {
    throw new Error(
      `ZIP is missing ${MANIFEST_FILE} — was it produced by the email export?`,
    );
  }
  const manifestText = await manifestEntry.async('string');
  const manifest = JSON.parse(manifestText) as {
    originalAttachments?: Array<{
      documentId: string;
      fileName: string;
      contentBase64: string;
      contentLength: number;
    }>;
  };

  const attachments = manifest.originalAttachments ?? [];
  for (const att of attachments) {
    const path = documentEntryPath(att.documentId, att.fileName);
    const entry = zip.file(path);
    if (!entry) {
      throw new Error(
        `ZIP is missing expected attachment at ${path} (documentId ${att.documentId})`,
      );
    }
    const bytes = await entry.async('uint8array');
    att.contentBase64 = uint8ArrayToBase64(bytes);
    att.contentLength = bytes.byteLength;
  }

  return manifest;
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  // btoa works on binary strings; chunk the conversion so we don't blow
  // the call stack on multi-MB attachments.
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(
      ...bytes.subarray(i, Math.min(i + CHUNK, bytes.length)),
    );
  }
  return btoa(binary);
}
